// Removed router-level experiment exposure for homepage; exposure will be logged inside the homepage component when it actually renders
import { Brand } from 'common/Brand';
import { ExperimentNames } from 'common/experiments/ExperimentDefinitions';
import { SessionStorageKeys, StorageKeys } from 'common/storage/constants';
import { QueryParams } from 'common/urls/QueryParams';
import { isNil } from 'lodash';
import React, { Suspense, useCallback, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { RouteChildrenProps } from 'react-router';
import { Switch } from 'react-router-dom';
import { LoginOrSignUpModal } from 'src/auth/modal/LoginOrSignUpModal';
import { useShowLoginOrSignUpModal } from 'src/auth/modal/useShowLoginOrSignUpModal';
import { useFeatureFlag } from 'src/feature-flags/consumers';
import { isNativeMobileApp } from 'src/mobile-app/isNativeMobileApp';
import { WSSReferralAdModal } from 'src/pages/consumer/dish-first/explore/components/Banners/ReferralBanners/WSSReferralAdModal';
import { isAppPath, PWA_START_PATH } from 'src/shared/appPaths';
import { brandPageMeta } from 'src/shared/brand/brandPageMeta';
import { useGoogleOneTapLogin } from 'src/shared/design-system/Login/useGoogleOneTapLogin';
import { useExperiment } from 'src/shared/hooks/experiments/useExperiment';
import { useIsImmersiveLandingPage } from 'src/shared/hooks/experiments/useImmersiveLandingPage';
import { useBrand } from 'src/shared/hooks/useBrand';
import { ConsumerRoutes, Routes } from 'src/shared/Routes';
import { getSessionStorage, useLocalStorage } from 'src/shared/storage/Storage';
import { isDevEnvironment, isStagingEnvironment } from 'src/shared/utils/EnvironmentUtilities';
import { locationInRoutes } from 'src/shared/utils/RouteUtilities';
import { useScopedReducer } from 'src/shef-global-state/shefGlobalState';
import { TrackedAuthRouteWithErrorBoundary } from 'src/tracking/authenticated-route';
import { TrackedRouteWithErrorBoundary } from 'src/tracking/tracked-route';
import { lazyRetry } from 'src/util/retry';
import { match as tsMatch } from 'ts-pattern';
import { StringParam, useQueryParam } from 'use-query-params';
import { LoadingScreenWithHeight } from 'web-common/src/shared/design-system/components/LoadingScreen';
import { ConsumerGroupOrderRatingModalContainer } from '../components/ratings/ConsumerGroupOrderRatingModal/ConsumerGroupOrderRatingModalContainer';
import { ZipCodeSelectionModalProvider } from './components/header-v2/ZipCodeSelection/ZipCodeSelectionModalContext';
import { SearchDataProvider } from './components/search/store/SearchDataProvider';
import { ConsumerBanner } from './ConsumerBanner';
import { OneTimeOrderingToast } from './dish-first/explore/ExplorePage/OneTimeOrderingToast';
import { useMealPlanSurfacingLogic } from './meal-plans/hooks/useMealPlanSurfacingLogic';
import { IntakeLoadingFrame } from './meal-plans/meal-plan-intake/preferences/steps/IntakeLoadingStep/IntakeLoadingFrame';
import { NotFoundPage } from './not-found/NotFoundPage';
import { tableRunBrandRoutes } from './tablerun/tableRunBrandRoutes';
import { TableRunConsumerRouter } from './tablerun/TableRunConsumerRouter';

const BecomeAShef = lazyRetry(() => import('src/pages/shef/become-a-shef/BecomeAShef'));
const Careers = lazyRetry(() => import('src/pages/careers/Careers'));
const CommunityGuidelines = lazyRetry(() => import('./static/ShefCommunityGuidelines'));
const ConsumerMultipleCartsDisplay = lazyRetry(() => import('./consumer-carts/ConsumerMultipleCartsDisplay'));
const ConsumerRefer = lazyRetry(() => import('src/pages/consumer/ConsumerRefer'));
const ConsumerExplore = lazyRetry(
  () => import(/* webpackPrefetch: true */ 'src/pages/consumer/dish-first/explore/ExplorePage')
);
const ConsumerExploreRedirect = lazyRetry(() => import('./explore/ConsumerExploreCuisineRedirectPage'));
const ConsumerExploreCuisine = lazyRetry(() => import('./explore-cuisine/ConsumerExploreCuisine'));
const ConsumerExploreMerchandise = lazyRetry(() => import('./explore-merchandise/ExploreMerchandise'));
const ConsumerFeePolicy = lazyRetry(() => import('./static/ConsumerFeePolicy'));
const ConsumerFollowing = lazyRetry(() => import('./follow-shef/FollowingPage'));
const ConsumerFooterWrapper = lazyRetry(() => import('./components/footer/ConsumerFooterWrapper'));
const ConsumerHeaderWrapper = lazyRetry(() => import('./components/ConsumerHeaderWrapper/ConsumerHeaderWrapper'));
const ConsumerLogin = lazyRetry(() => import('src/auth/page/LoginPage'));
const MigratedShefRedirect = lazyRetry(() => import('src/pages/consumer/order/MigratedShefRedirect'));
const ConsumerOrderWSSShef = lazyRetry(() => import('src/pages/consumer/order/ConsumerOrderWSSShef'));
const ConsumerOrderCartMobile = lazyRetry(() => import('src/cart/ConsumerOrderCartMobile'));
const ConsumerOrderEditDelivery = lazyRetry(() => import('./order/ConsumerOrderEditDelivery'));
const ConsumerOrderEditLineItems = lazyRetry(() => import('./order/ConsumerOrderEditLineItems'));
const GroupOrderEditLineItems = lazyRetry(() => import('./order/GroupOrderEditLineItems'));
const ConsumerOrderEditCart = lazyRetry(() => import('./order/ConsumerOrderEditCart'));
const ConsumerOrderCancel = lazyRetry(() => import('./order/ConsumerOrderCancel'));
const ReorderEndpoint = lazyRetry(() => import('./order/reorder/ReorderEndpoint'));
const ConsumerOrderReceipt = lazyRetry(() => import('./order/receipt/ConsumerOrderReceipt'));
const ConsumerOrderFinalize = lazyRetry(() => import('src/checkout/ConsumerOrderFinalize'));
const ConsumerOrdersList = lazyRetry(() => import('./account/orders'));
const ConsumerGiftCardPurchase = lazyRetry(() => import('./ConsumerGiftCardPurchase'));
const ConsumerDonate = lazyRetry(() => import('./donate'));
const ConsumerDonateThanks = lazyRetry(() => import('./donate/thanks'));
const ConsumerMessagesPage = lazyRetry(() => import('./account/messages/ConsumerMessagesPage'));
const ConsumerOrderSearch = lazyRetry(() => import('./search/SearchResults'));
const ConsumerMealPlanLandingPage = lazyRetry(() => import('./meal-plans/MealPlanRefreshLandingPage'));
const ConsumerMealPlanPreferences = lazyRetry(() => import('./meal-plans/meal-plan-intake/preferences'));
const ConsumerMealPlanCheckout = lazyRetry(() => import('./meal-plans/meal-plan-intake/checkout'));
const ConsumerMealPlanEdit = lazyRetry(() => import('./meal-plans/meal-plan-intake/edit'));
const ConsumerMealPlanUpcomingGroupOrders = lazyRetry(() => import('./meal-plans/upcoming-order'));
const ConsumerMealPlansManage = lazyRetry(() => import('./meal-plans/manage/index'));
const ConsumerMealPlanManageServingsPage = lazyRetry(() => import('./meal-plans/manage/servings'));
const ConsumerMealPlanManageSchedulePage = lazyRetry(() => import('./meal-plans/manage/schedule'));
const ConsumerMealPlanManagePersonalInfo = lazyRetry(() => import('./meal-plans/manage/personal-info'));
const ConsumerMealPlansManageTasteProfile = lazyRetry(() => import('./meal-plans/manage/taste-profile'));
const ConsumerMealPlansManagePause = lazyRetry(() => import('./meal-plans/manage/pause'));
const ConsumerMealPlansManagePackaging = lazyRetry(() => import('./meal-plans/manage/packaging'));
const ConsumerMealPlansManageCancel = lazyRetry(() => import('./meal-plans/manage/cancel'));

const ConsumerPrivacyPolicy = lazyRetry(() => import('./static/ConsumerPrivacyPolicy'));
const ConsumerProfile = lazyRetry(() => import('./account/ConsumerProfile'));
const ConsumerNotifications = lazyRetry(() => import('./account/notifications/ConsumerNotifications'));
const ConsumerPromoHandler = lazyRetry(() => import('./components/ConsumerPromoHandler'));
const ConsumerResetChangePassword = lazyRetry(() => import('src/auth/page/ConsumerResetChangePassword'));
const ConsumerShare = lazyRetry(() => import('./account/refer/Refer'));
const ConsumerShareShef = lazyRetry(() => import('./account/refer/ReferShef'));
const ConsumerShareFoodItem = lazyRetry(() => import('./account/refer/ReferFoodItem'));
const ConsumerShareOrder = lazyRetry(() => import('./account/refer/ReferOrder'));
const ConsumerViewReferOrder = lazyRetry(() => import('./account/refer/ReferOrder/ViewReferOrder'));
const ConsumerTos = lazyRetry(() => import('./static/ConsumerTos'));
const ConsumerJun2022PickupOnlyTransitonInfo = lazyRetry(
  () => import('./static/ConsumerJun2022PickupOnlyTransitonInfo')
);
const Homepage = lazyRetry(() => import('./homepage'));
const ConsumerPaymentMethods = lazyRetry(() => import('./account/payment-methods/ConsumerPaymentMethods'));
const ConsumerDeliveryAddresses = lazyRetry(() => import('./account/delivery-addresses/ConsumerDeliveryAddresses'));
const ConsumerTableRunSubscriptions = lazyRetry(() => import('./account/tablerun-subscriptions'));
const WaitlistHome = lazyRetry(() => import('src/waitlists/WaitlistHome'));
const WaitlistActions = lazyRetry(() => import('src/waitlists/WaitlistActions'));
const WaitlistReferral = lazyRetry(() => import('src/waitlists/WaitlistReferral'));

const CelebrityEvent = lazyRetry(() => import('src/celebrity-event/CelebrityEventPage'));
const TableRunHeader = lazyRetry(() => import('src/pages/consumer/tablerun/components/TableRunHeader'));

export const ConsumerRouter: React.FC<RouteChildrenProps> = ({ match, history, location }) => {
  const [groupOrderIdForReview, setGroupOrderIdForReview] = useQueryParam(
    QueryParams.RATE_PREV_GROUP_ORDER_ID,
    StringParam
  );
  const { setVisitedMealPlans, visitedMealPlans } = useMealPlanSurfacingLogic();

  const isOnHeaderlessRoute = locationInRoutes(location, [
    ConsumerRoutes.CONSUMER_REGISTER,
    ConsumerRoutes.CONSUMER_LOGIN,
    ConsumerRoutes.CONSUMER_TABLERUN_STOREFRONT,
    ConsumerRoutes.CONSUMER_TABLERUN_CHECKOUT,
    ConsumerRoutes.CONSUMER_TABLERUN_CONFIRMATION,
    ConsumerRoutes.CONSUMER_TABLERUN_SIGNUP,
    ConsumerRoutes.CONSUMER_TABLERUN_DIRECTORY,
    ConsumerRoutes.CONSUMER_MEAL_PLANS_MANAGE_SERVINGS,
    ConsumerRoutes.CONSUMER_MEAL_PLANS_MANAGE_SCHEDULE,
    ConsumerRoutes.CONSUMER_MEAL_PLANS_MANAGE,
    ConsumerRoutes.CONSUMER_MEAL_PLANS_MANAGE_TASTE_PROFILE,
    ConsumerRoutes.CONSUMER_MEAL_PLANS_MANAGE_PERSONAL_INFO,
  ]);
  const brand = useBrand();
  const inTableRunMode = brand === Brand.TableRun;
  const pageMeta = brandPageMeta({ brand, isDev: isDevEnvironment(), isStaging: isStagingEnvironment() });
  const isImmersiveLandingPage = useIsImmersiveLandingPage();
  const isOnUnknownRoute = !inTableRunMode && !isAppPath(location.pathname);
  const hideHeader = isOnHeaderlessRoute || isImmersiveLandingPage;
  const isOnSubscriptionsIntake = locationInRoutes(location, [
    Routes.CONSUMER_MEAL_PLANS,
    Routes.CONSUMER_MEAL_PLANS_CHECKOUT,
    Routes.CONSUMER_MEAL_PLANS_EDIT_ORDER,
    Routes.CONSUMER_MEAL_PLANS_UPCOMING_ORDER,
    Routes.CONSUMER_MEAL_PLANS_MANAGE,
    Routes.CONSUMER_MEAL_PLANS_MANAGE_PLAN,
    Routes.CONSUMER_MEAL_PLANS_MANAGE_TASTE_PROFILE,
    Routes.CONSUMER_MEAL_PLANS_PREFERENCES,
    Routes.CONSUMER_MEAL_PLANS_CHECKOUT,
    Routes.CONSUMER_MEAL_PLANS_MANAGE_PAUSE,
    Routes.CONSUMER_MEAL_PLANS_CANCELLATION_REASONS,
  ]);
  const isOnDonateRoute = locationInRoutes(location, [Routes.CONSUMER_DONATE, Routes.CONSUMER_DONATE_THANKS]);

  const { isTreatment: isIntakeOptimizationTreatment } = useExperiment(ExperimentNames.INTAKE_OPTIMIZATION);
  const isEnteringIntake = locationInRoutes(location, [Routes.CONSUMER_MEAL_PLANS_PREFERENCES]);
  const isEnteringIntakeCheckout =
    isIntakeOptimizationTreatment &&
    locationInRoutes(location, [Routes.CONSUMER_MEAL_PLANS_CHECKOUT]) &&
    !isNil(getSessionStorage().getItem(SessionStorageKeys.INTAKE_CHECKLIST_LINES));

  useEffect(() => {
    if (!isIntakeOptimizationTreatment) return;
    ConsumerMealPlanPreferences.preload?.();
    ConsumerMealPlanCheckout.preload?.();
  }, [isIntakeOptimizationTreatment]);

  const showManageMealPlans = useFeatureFlag('showManageMealPlans');
  const reusablePackagingEnabled = useFeatureFlag('reusablePackagingEnabled');
  const wssReusableOptInEnabled = useFeatureFlag('wssReusableOptInEnabled');
  const neighborsFeedingNeighborsEnabled = useFeatureFlag('neighborsFeedingNeighborsEnabled');
  // Keep track of urls for `useHomepageRedirects`
  const [, setLastVisitedUrl] = useLocalStorage<string>(StorageKeys.LAST_VISITED_URL, '');
  useEffect(() => {
    if (location.pathname !== Routes.CONSUMER_LANDING) {
      setLastVisitedUrl(location.pathname);
    }
    if (isOnSubscriptionsIntake && !visitedMealPlans) {
      setVisitedMealPlans(true);
    }
  }, [location.pathname, visitedMealPlans, isOnSubscriptionsIntake, setLastVisitedUrl, setVisitedMealPlans]);

  const hideGroupOrderRatingModal = useCallback(() => setGroupOrderIdForReview(undefined), [setGroupOrderIdForReview]);

  const dontRenderConsumerClass =
    match?.path === Routes.CONSUMER_LANDING || match?.path === Routes.CONSUMER_BECOME_A_SHEF;

  /** * Try to extract promo code from either referral url or promo param and put into PromoContext * */

  const backgroundRef = useRef<HTMLDivElement>(null);

  const { state: configs } = useScopedReducer('configs');
  const { state: isGlobalStateLoaded } = useScopedReducer('loaded');
  const { profilePaymentMethodsEnabled, profileDeliveryAddressesEnabled, giftCardPurchaseEnabled, chatEnabled } =
    configs;

  useGoogleOneTapLogin(location);
  const { hide, isShowing } = useShowLoginOrSignUpModal();

  const header = (
    <div className='consumer shrink-0 grow-0'>
      <ConsumerPromoHandler location={location} history={history} match={match} />
      <ConsumerHeaderWrapper
        location={location}
        backgroundRef={backgroundRef}
        hideSearchAndSignup={isOnSubscriptionsIntake || isOnDonateRoute}
      />
    </div>
  );

  const headerSlot = tsMatch({ hideHeader, isImmersiveLandingPage, inTableRunMode })
    .with({ hideHeader: true, isImmersiveLandingPage: true }, () => (
      <ConsumerPromoHandler location={location} history={history} match={match} />
    ))
    .with({ hideHeader: true }, () => null)
    .with({ inTableRunMode: true }, () => <TableRunHeader />)
    .otherwise(() => header);

  if (isOnUnknownRoute) {
    return <NotFoundPage />;
  }

  return (
    <SearchDataProvider>
      <ZipCodeSelectionModalProvider>
        <Helmet>
          <title>{pageMeta.title}</title>
          <meta name='description' content={pageMeta.description} />
        </Helmet>
        <OneTimeOrderingToast location={location} isOnSubscriptionIntake={isOnSubscriptionsIntake} />
        <div className='flex min-h-screen flex-col'>
          {headerSlot}
          <LoginOrSignUpModal hide={hide} isShowing={isShowing} />
          <div ref={backgroundRef} className='flex grow flex-col'>
            <ConsumerBanner />
            <div className={`flex grow flex-col${dontRenderConsumerClass ? '' : 'consumer'}`}>
              {groupOrderIdForReview ? (
                <ConsumerGroupOrderRatingModalContainer
                  groupOrderId={groupOrderIdForReview}
                  hide={hideGroupOrderRatingModal}
                />
              ) : null}
              <WSSReferralAdModal />

              <Suspense
                fallback={tsMatch({
                  entersIntakeOnLoader: isIntakeOptimizationTreatment && isEnteringIntake,
                  isEnteringIntakeCheckout,
                })
                  .with({ entersIntakeOnLoader: true }, () => <IntakeLoadingFrame />)
                  .with({ isEnteringIntakeCheckout: true }, () => null)
                  .otherwise(() => (
                    <LoadingScreenWithHeight />
                  ))}>
                {inTableRunMode ? (
                  <TableRunConsumerRouter />
                ) : (
                  <Switch>
                    <TrackedRouteWithErrorBoundary path={Routes.CAREERS} component={Careers} />
                    <TrackedRouteWithErrorBoundary path={Routes.COMMUNITY_GUIDELINES} component={CommunityGuidelines} />
                    <TrackedRouteWithErrorBoundary
                      path={Routes.DEPRECATED_CONSUMER_COMING_SOON}
                      component={WaitlistHome}
                    />

                    <TrackedRouteWithErrorBoundary
                      path={Routes.CONSUMER_ACCOUNT_SHARE_ORDER}
                      exact
                      component={ConsumerShareOrder}
                    />

                    <TrackedRouteWithErrorBoundary
                      path={Routes.CONSUMER_MEAL_PLANS}
                      exact
                      component={ConsumerMealPlanLandingPage}
                    />

                    <TrackedRouteWithErrorBoundary
                      path={Routes.CONSUMER_MEAL_PLANS_PREFERENCES}
                      exact
                      component={ConsumerMealPlanPreferences}
                    />

                    <TrackedRouteWithErrorBoundary
                      path={Routes.CONSUMER_MEAL_PLANS_CHECKOUT}
                      exact
                      component={ConsumerMealPlanCheckout}
                    />

                    <TrackedAuthRouteWithErrorBoundary
                      path={Routes.CONSUMER_MEAL_PLANS_EDIT_ORDER}
                      exact
                      component={ConsumerMealPlanEdit}
                      loginHeader='Log in or sign up to edit your meal plan'
                    />

                    <TrackedAuthRouteWithErrorBoundary
                      path={Routes.CONSUMER_MEAL_PLANS_UPCOMING_ORDER}
                      exact
                      component={ConsumerMealPlanUpcomingGroupOrders}
                      loginHeader='Log in or sign up to see your upcoming order'
                    />

                    <TrackedRouteWithErrorBoundary path={Routes.CONSUMER_REFER} exact component={ConsumerRefer} />
                    <TrackedRouteWithErrorBoundary
                      path={Routes.CONSUMER_REFER_ORDER}
                      exact
                      component={ConsumerViewReferOrder}
                    />
                    <TrackedRouteWithErrorBoundary path={Routes.CONSUMER_REFER_SHEF} exact component={ConsumerRefer} />
                    <TrackedRouteWithErrorBoundary
                      path={Routes.CONSUMER_REFER_FOOD_ITEM}
                      exact
                      component={ConsumerRefer}
                    />
                    <TrackedRouteWithErrorBoundary path={Routes.CONSUMER_LOGIN} component={ConsumerLogin} />
                    <TrackedRouteWithErrorBoundary path={Routes.CONSUMER_REGISTER} component={ConsumerLogin} />
                    <TrackedRouteWithErrorBoundary
                      path={Routes.CONSUMER_RESET_CHANGE_PASSWORD}
                      component={ConsumerResetChangePassword}
                    />

                    <TrackedRouteWithErrorBoundary
                      path={Routes.CONSUMER_EXPLORE_CUISINE}
                      component={ConsumerExploreRedirect}
                      suppressPageView
                    />
                    <TrackedRouteWithErrorBoundary path={Routes.CONSUMER_EXPLORE} component={ConsumerExplore} />
                    <TrackedRouteWithErrorBoundary
                      path={Routes.CONSUMER_EXPLORE_MERCHANDISE}
                      component={ConsumerExploreMerchandise}
                    />
                    <TrackedRouteWithErrorBoundary
                      path={Routes.CONSUMER_MULTIPLE_CARTS_VIEW}
                      component={ConsumerMultipleCartsDisplay}
                    />
                    <TrackedRouteWithErrorBoundary
                      path={Routes.CONSUMER_ORDER_ONE_TIME}
                      exact
                      component={MigratedShefRedirect}
                    />
                    <TrackedRouteWithErrorBoundary
                      path={Routes.CONSUMER_ORDER_FOOD_ITEM}
                      exact
                      component={MigratedShefRedirect}
                    />
                    <TrackedRouteWithErrorBoundary
                      path={Routes.CONSUMER_ORDER_WSS_SHEF}
                      exact
                      component={ConsumerOrderWSSShef}
                    />

                    <TrackedRouteWithErrorBoundary
                      path={Routes.CONSUMER_ORDER_SEARCH}
                      exact
                      component={ConsumerOrderSearch}
                    />
                    <TrackedRouteWithErrorBoundary
                      path={Routes.CONSUMER_ORDER_CART}
                      component={ConsumerOrderCartMobile}
                    />
                    <TrackedRouteWithErrorBoundary
                      path={Routes.CONSUMER_ORDER_FINALIZE}
                      component={ConsumerOrderFinalize}
                    />
                    <TrackedRouteWithErrorBoundary path={Routes.CONSUMER_ACCOUNT_PROFILE} component={ConsumerProfile} />
                    <TrackedRouteWithErrorBoundary
                      path={Routes.CONSUMER_ACCOUNT_SHARE}
                      exact
                      component={ConsumerShare}
                    />
                    <TrackedRouteWithErrorBoundary
                      path={Routes.CONSUMER_ACCOUNT_SHARE_SHEF}
                      exact
                      component={ConsumerShareShef}
                    />
                    <TrackedRouteWithErrorBoundary
                      path={Routes.CONSUMER_ACCOUNT_SHARE_FOOD_ITEM}
                      exact
                      component={ConsumerShareFoodItem}
                    />
                    {profilePaymentMethodsEnabled && (
                      <TrackedRouteWithErrorBoundary
                        path={Routes.CONSUMER_ACCOUNT_PAYMENT_METHODS}
                        component={ConsumerPaymentMethods}
                      />
                    )}
                    {profileDeliveryAddressesEnabled && (
                      <TrackedRouteWithErrorBoundary
                        path={Routes.CONSUMER_ACCOUNT_DELIVERY_ADDRESSES}
                        component={ConsumerDeliveryAddresses}
                      />
                    )}
                    <TrackedRouteWithErrorBoundary
                      path={Routes.CONSUMER_ACCOUNT_TABLERUN_SUBSCRIPTIONS}
                      component={ConsumerTableRunSubscriptions}
                    />
                    <TrackedRouteWithErrorBoundary
                      path={Routes.CONSUMER_ACCOUNT_NOTIFICATIONS}
                      component={ConsumerNotifications}
                    />
                    {giftCardPurchaseEnabled && (
                      <TrackedRouteWithErrorBoundary
                        path={Routes.CONSUMER_GIFT_CARD}
                        component={ConsumerGiftCardPurchase}
                      />
                    )}
                    {neighborsFeedingNeighborsEnabled && (
                      <TrackedRouteWithErrorBoundary path={Routes.CONSUMER_DONATE} exact component={ConsumerDonate} />
                    )}
                    {neighborsFeedingNeighborsEnabled && (
                      <TrackedRouteWithErrorBoundary
                        path={Routes.CONSUMER_DONATE_THANKS}
                        exact
                        component={ConsumerDonateThanks}
                      />
                    )}
                    <TrackedRouteWithErrorBoundary
                      path={Routes.CONSUMER_ACCOUNT_ORDERS}
                      component={ConsumerOrdersList}
                    />
                    {chatEnabled && (
                      <TrackedRouteWithErrorBoundary
                        path={Routes.CONSUMER_ACCOUNT_MESSAGES}
                        component={ConsumerMessagesPage}
                      />
                    )}
                    <TrackedRouteWithErrorBoundary
                      path={Routes.CONSUMER_ORDER_EDIT_DELIVERY}
                      component={ConsumerOrderEditDelivery}
                    />
                    <TrackedRouteWithErrorBoundary
                      path={Routes.CONSUMER_ORDER_EDIT_FOOD_ITEM}
                      exact
                      component={ConsumerOrderEditLineItems}
                    />
                    <TrackedRouteWithErrorBoundary
                      path={Routes.CONSUMER_ORDER_EDIT_GROUP_ORDER_FOOD_ITEM}
                      component={GroupOrderEditLineItems}
                    />
                    <TrackedRouteWithErrorBoundary
                      path={Routes.CONSUMER_ORDER_GROUP_ORDER_EDIT}
                      component={GroupOrderEditLineItems}
                    />
                    <TrackedRouteWithErrorBoundary
                      path={Routes.CONSUMER_ORDER_EDIT_LINE_ITEMS}
                      component={ConsumerOrderEditLineItems}
                    />
                    <TrackedRouteWithErrorBoundary
                      path={Routes.CONSUMER_ORDER_EDIT_CART}
                      component={ConsumerOrderEditCart}
                    />
                    <TrackedRouteWithErrorBoundary
                      path={Routes.CONSUMER_ORDER_CANCEL}
                      component={ConsumerOrderCancel}
                    />
                    <TrackedRouteWithErrorBoundary path={Routes.CONSUMER_REORDER} component={ReorderEndpoint} />
                    <TrackedRouteWithErrorBoundary
                      path={Routes.CONSUMER_ORDER_CONFIRMATION}
                      component={ConsumerOrderReceipt}
                    />
                    <TrackedRouteWithErrorBoundary
                      path={Routes.CONSUMER_ORDERS_CONFIRMATION}
                      component={ConsumerOrderReceipt}
                    />
                    <TrackedRouteWithErrorBoundary
                      path={Routes.CONSUMER_ORDER_RECEIPT}
                      component={ConsumerOrderReceipt}
                    />
                    <TrackedRouteWithErrorBoundary
                      path={Routes.CONSUMER_PRIVACY_POLICY}
                      component={ConsumerPrivacyPolicy}
                    />
                    <TrackedRouteWithErrorBoundary path={Routes.CONSUMER_TOS} component={ConsumerTos} />
                    <TrackedRouteWithErrorBoundary
                      path={Routes.CONSUMER_JUN_2022_PICKUP_ONLY_MARKET_INFO}
                      component={ConsumerJun2022PickupOnlyTransitonInfo}
                    />
                    <TrackedRouteWithErrorBoundary path={Routes.CONSUMER_FEE_POLICY} component={ConsumerFeePolicy} />
                    <TrackedRouteWithErrorBoundary
                      path={Routes.CONSUMER_BECOME_A_SHEF_REGISTER}
                      component={BecomeAShef}
                    />
                    <TrackedRouteWithErrorBoundary path={Routes.CONSUMER_BECOME_A_SHEF} component={BecomeAShef} />
                    <TrackedRouteWithErrorBoundary path={Routes.CONSUMER_FOLLOWING} component={ConsumerFollowing} />
                    <TrackedRouteWithErrorBoundary
                      path={Routes.CONSUMER_EXPLORE_CUISINE_SEARCH}
                      component={ConsumerExploreCuisine}
                    />
                    {/* Keep this ordering for waitlist routes */}
                    <TrackedRouteWithErrorBoundary path={Routes.WAITLIST_ACTIONS_REFER} component={WaitlistReferral} />
                    <TrackedRouteWithErrorBoundary path={Routes.WAITLIST_ACTIONS} component={WaitlistActions} />
                    <TrackedRouteWithErrorBoundary path={Routes.WAITLIST_HOME} component={WaitlistHome} />
                    {showManageMealPlans && (
                      <TrackedAuthRouteWithErrorBoundary
                        path={Routes.CONSUMER_MEAL_PLANS_MANAGE}
                        exact
                        component={ConsumerMealPlansManage}
                        loginHeader='Log in or sign up to manage your meal plan'
                      />
                    )}

                    {showManageMealPlans && (
                      <TrackedAuthRouteWithErrorBoundary
                        path={Routes.CONSUMER_MEAL_PLANS_MANAGE_SERVINGS}
                        exact
                        component={ConsumerMealPlanManageServingsPage}
                        loginHeader='Log in or sign up to manage your meal plan'
                      />
                    )}

                    {showManageMealPlans && (
                      <TrackedAuthRouteWithErrorBoundary
                        path={Routes.CONSUMER_MEAL_PLANS_MANAGE_SCHEDULE}
                        exact
                        component={ConsumerMealPlanManageSchedulePage}
                        loginHeader='Log in or sign up to manage your meal plan'
                      />
                    )}

                    {showManageMealPlans && (
                      <TrackedAuthRouteWithErrorBoundary
                        path={Routes.CONSUMER_MEAL_PLANS_MANAGE_TASTE_PROFILE}
                        exact
                        component={ConsumerMealPlansManageTasteProfile}
                        loginHeader='Log in or sign up to manage your weekly service'
                      />
                    )}

                    {showManageMealPlans && (
                      <TrackedAuthRouteWithErrorBoundary
                        path={Routes.CONSUMER_MEAL_PLANS_MANAGE_PERSONAL_INFO}
                        exact
                        component={ConsumerMealPlanManagePersonalInfo}
                        loginHeader='Log in or sign up to manage your weekly service'
                      />
                    )}

                    {
                      <TrackedAuthRouteWithErrorBoundary
                        path={Routes.CONSUMER_MEAL_PLANS_MANAGE_PAUSE}
                        exact
                        component={ConsumerMealPlansManagePause}
                      />
                    }

                    {reusablePackagingEnabled && wssReusableOptInEnabled && (
                      <TrackedAuthRouteWithErrorBoundary
                        path={Routes.CONSUMER_MEAL_PLANS_MANAGE_PACKAGING}
                        exact
                        component={ConsumerMealPlansManagePackaging}
                      />
                    )}

                    {
                      <TrackedRouteWithErrorBoundary
                        path={Routes.CONSUMER_MEAL_PLANS_CANCELLATION_REASONS}
                        exact
                        component={ConsumerMealPlansManageCancel}
                      />
                    }

                    {/* Celebrity Events */}
                    <TrackedRouteWithErrorBoundary path={Routes.BINGING_WITH_BABISH} component={CelebrityEvent} />
                    <TrackedRouteWithErrorBoundary path={Routes.MASTER_CHEF_NYESHA} component={CelebrityEvent} />

                    {tableRunBrandRoutes()}

                    <TrackedRouteWithErrorBoundary
                      path={[Routes.CONSUMER_LANDING, PWA_START_PATH]}
                      exact
                      component={Homepage}
                    />

                    {/* Needs to be last. Catches paths the server serves but no route above renders. Feature flags
                        start false, so flag-gated routes land here until they load */}
                    <TrackedRouteWithErrorBoundary
                      component={isGlobalStateLoaded ? NotFoundPage : LoadingScreenWithHeight}
                      suppressPageView
                    />
                  </Switch>
                )}
              </Suspense>
            </div>
          </div>

          {isNativeMobileApp() || isOnHeaderlessRoute ? null : (
            <ConsumerFooterWrapper history={history} location={location} backgroundRef={backgroundRef} />
          )}
        </div>
      </ZipCodeSelectionModalProvider>
    </SearchDataProvider>
  );
};

export default ConsumerRouter;
